// src/components/NavBar.jsx

import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import AuthModal from "./AuthModal";
import { getSubscriptionStatus } from "../utils/api";
import { UserCircle, Menu, X } from "lucide-react";

export default function NavBar() {
  const { user, refreshUserWithToken } = useAuthContext();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [hasActiveSub, setHasActiveSub] = useState(false);
  const [isPreviewTier, setIsPreviewTier] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check subscription status whenever user changes
  useEffect(() => {
    if (user) {
      (async () => {
        try {
          const { data } = await getSubscriptionStatus();
          setHasActiveSub(data.active);
          // Check if user is on Preview tier (must have active subscription AND be on preview)
          // Users without any subscription (active=false) are NOT preview tier
          const isPreview = data.active && (data.plan_tier === 'preview');
          setIsPreviewTier(isPreview);
        } catch (err) {
          console.error("Failed to fetch subscription status:", err);
          setHasActiveSub(false);
          setIsPreviewTier(false);
        }
      })();
    } else {
      setHasActiveSub(false);
      setIsPreviewTier(false);
    }
  }, [user]);

  function handleLoginClick() {
    setAuthMode("login");
    setShowAuth(true);
  }

  function handleSignUpClick() {
    setAuthMode("register");
    setShowAuth(true);
  }

  function handleLogout() {
    localStorage.removeItem("jwt_token");
    refreshUserWithToken(null);
    setHasActiveSub(false);
    navigate("/");
  }

  // Determine link target/text for pricing page
  // Preview tier users should still see the link to upgrade
  const subLink = user
    ? (hasActiveSub && !isPreviewTier)
      ? null // Don't show pricing link for paid subscribers - it's on profile page
      : { to: "/subscribe", label: isPreviewTier ? "Upgrade" : "Pricing" }
    : { to: "/subscribe", label: "Pricing" };

  const scrollToSection = (sectionId) => {
    setIsMenuOpen(false);
    
    // If we're on the home page, scroll directly
    if (location.pathname === '/') {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
        // Update URL hash without page reload
        window.history.pushState(null, '', `/#${sectionId}`);
      }
    } else {
      // Navigate to home with hash - the Hero component will handle scrolling
      navigate(`/#${sectionId}`);
    }
  };

  useEffect(() => {
    if (location.state?.scrollTo) {
      const section = document.getElementById(location.state.scrollTo);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
        navigate(location.pathname, { state: {} });
      }
    }
  }, [location, navigate]);

  return (
    <header className="sticky top-0 z-50 bg-white shadow-lg border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 sm:h-18">
        {/* Logo - Hidden on mobile to save space */}
        <div className="flex-shrink-0 hidden sm:block">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/favicon.ico" alt="Logo" className="h-8 w-auto" />
            <span className="font-bold text-xl text-gray-800 hidden lg:block">Cards</span>
          </Link>
        </div>

        {/* Navigation - Some items visible on mobile */}
        <nav className="flex items-center">
          {/* Always visible items */}
          <div className="flex items-center space-x-3 sm:space-x-4 md:space-x-8">
            <button onClick={() => scrollToSection('templates')} className="text-sm md:text-base font-medium text-gray-700 hover:text-purple-600 transition-colors px-2 py-1 rounded-md hover:bg-purple-50">
              Templates
            </button>
            {subLink && (
              <Link to={subLink.to} className="text-sm md:text-base font-medium text-gray-700 hover:text-purple-600 transition-colors px-2 py-1 rounded-md hover:bg-purple-50">
                {subLink.label}
              </Link>
            )}
            {/* Help moved to desktop-only and mobile menu */}
            <Link to="/help" className="hidden md:block text-sm md:text-base font-medium text-gray-700 hover:text-purple-600 transition-colors px-2 py-1 rounded-md hover:bg-purple-50">
              Help
            </Link>
          </div>

          {/* Desktop-only items */}
          <div className="hidden md:flex items-center space-x-4 ml-8">
            <button onClick={() => scrollToSection('hero')} className="text-base font-medium text-gray-700 hover:text-purple-600 transition-colors px-2 py-1 rounded-md hover:bg-purple-50">
              Home
            </button>
            <button onClick={() => scrollToSection('preview')} className="text-base font-medium text-gray-700 hover:text-purple-600 transition-colors px-2 py-1 rounded-md hover:bg-purple-50">
              Preview
            </button>
            <Link to="/articles" className="text-base font-medium text-gray-700 hover:text-purple-600 transition-colors px-2 py-1 rounded-md hover:bg-purple-50">
              Articles
            </Link>
          </div>
        </nav>

        {/* Mobile Auth - Always visible with bigger buttons */}
        <div className="flex md:hidden items-center space-x-3">
          {user ? (
            <>
              <Link 
                to="/profile"
                className="flex items-center text-gray-700 hover:text-purple-600 p-2 rounded-full hover:bg-purple-50 transition-colors"
              >
                <UserCircle className="h-7 w-7" />
              </Link>
              <button
                onClick={handleLogout}
                className="px-4 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg text-sm font-semibold shadow-lg hover:from-purple-600 hover:to-purple-700 hover:shadow-xl transition-all duration-200"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleLoginClick}
                className="px-4 py-2.5 border-2 border-purple-600 text-purple-600 rounded-lg text-sm font-semibold hover:bg-purple-50 hover:border-purple-700 transition-all duration-200"
              >
                Login
              </button>
              <button
                onClick={handleSignUpClick}
                className="px-4 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg text-sm font-semibold shadow-lg hover:from-purple-600 hover:to-purple-700 hover:shadow-xl transition-all duration-200"
              >
                Sign up
              </button>
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <button 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden p-2 rounded-lg text-gray-700 hover:text-purple-600 hover:bg-purple-50 transition-colors ml-2"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Desktop Auth section */}
        <div className="hidden md:flex items-center space-x-4">
          {user ? (
            <>
              <Link 
                to="/profile"
                className="flex items-center gap-2 text-gray-700 hover:text-purple-600 px-3 py-2 rounded-lg hover:bg-purple-50 transition-colors"
              >
                <UserCircle className="h-6 w-6" />
                <span className="text-base font-medium truncate max-w-[120px] lg:max-w-none">{user.email}</span>
              </Link>
              <button
                onClick={handleLogout}
                className="px-5 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg font-semibold shadow-lg hover:from-purple-600 hover:to-purple-700 hover:shadow-xl transition-all duration-200"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleLoginClick}
                className="px-5 py-2.5 border-2 border-purple-600 text-purple-600 rounded-lg font-semibold hover:bg-purple-50 hover:border-purple-700 transition-all duration-200"
              >
                Login
              </button>
              <button
                onClick={handleSignUpClick}
                className="px-5 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg font-semibold shadow-lg hover:from-purple-600 hover:to-purple-700 hover:shadow-xl transition-all duration-200"
              >
                Sign up
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-3 space-y-1">
            <button onClick={() => scrollToSection('hero')} className="block w-full text-left py-3 px-3 text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-lg font-medium transition-colors">
              Home
            </button>
            <button onClick={() => scrollToSection('preview')} className="block w-full text-left py-3 px-3 text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-lg font-medium transition-colors">
              Preview
            </button>
            <Link 
              to="/articles" 
              className="block py-3 px-3 text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-lg font-medium transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Articles
            </Link>
            <Link 
              to="/help" 
              className="block py-3 px-3 text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-lg font-medium transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Help
            </Link>
            {user && (
              <Link 
                to="/profile" 
                className="block py-3 px-3 text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-lg font-medium transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Profile ({user.email})
              </Link>
            )}
          </div>
        </div>
      )}

      {showAuth && (
        <AuthModal 
          initialTab={authMode} 
          onClose={() => {
            setShowAuth(false);
            if (user) {
              getSubscriptionStatus().then(({ data }) => {
                setHasActiveSub(data.active);
                // Users must have active subscription AND be on preview tier
                const isPreview = data.active && (data.plan_tier === 'preview');
                setIsPreviewTier(isPreview);
              }).catch(console.error);
            }
          }} 
        />
      )}
    </header>
  );
}
